/**
 * Project detail image lightbox.
 *
 * Dependency-free. Each project gallery ([data-lightgallery]) is a set of
 * thumbnail links ([data-lightbox]) whose href is the full-size image. Clicking
 * one zooms that thumbnail up to full size (a FLIP animation), then fades in the
 * backdrop and chrome; closing reverses it back into the grid. Close button and
 * image share one flex column, so the "× schliessen" label lines up with the
 * image's edge by layout alone; the "1/8" index is fixed bottom-center on screen.
 *
 * Navigate by clicking the image (left half previous, right half next) or
 * swiping; arrow keys and Esc work too.
 */

const galleries = document.querySelectorAll('[data-lightgallery]')

if (galleries.length) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const ZOOM_MS = 280 // snappy zoom
  const EASING = 'cubic-bezier(0.59, 0.35, 0.22, 1)' // matches --ease-in

  // Single reusable overlay for every gallery on the page.
  const overlay = document.createElement('div')
  overlay.className = 'lightbox'
  overlay.dataset.lbOverlay = ''
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', 'Projekt-Bilder')
  overlay.hidden = true
  overlay.innerHTML = `
    <div class="lightbox-backdrop" data-lb-backdrop></div>
    <figure class="lightbox-figure" data-lb-figure>
      <button type="button" class="lightbox-close font-mono fs-12 hidden-sr" data-lb-close>× schliessen</button>
      <img class="lightbox-image" data-lb-image alt="" draggable="false" />
      <div class="lightbox-index font-mono fs-12" data-lb-index></div>
    </figure>`
  document.body.appendChild(overlay)

  const backdrop = overlay.querySelector('[data-lb-backdrop]')
  const figure = overlay.querySelector('[data-lb-figure]')
  const image = overlay.querySelector('[data-lb-image]')
  const indexEl = overlay.querySelector('[data-lb-index]')
  const closeBtn = overlay.querySelector('[data-lb-close]')

  let items = []
  let current = 0
  let lastFocus = null
  let anims = []

  const stopAnims = () => {
    anims.forEach((a) => a.cancel())
    anims = []
  }

  // Show the already-decoded grid thumbnail immediately. aspect-ratio holds the
  // box before pixels arrive, so the figure never collapses and the labels never
  // jump between slides.
  const showPreview = (item) => {
    // Size the box from the ratio (see .lightbox-image), NOT the loaded image's
    // intrinsic size — otherwise the box would be the thumbnail's size during the
    // zoom and then jump to the full image's size when it swaps in. Fixing the box
    // makes the FLIP land exactly and the full-image swap seamless.
    if (item.width && item.height) {
      image.style.setProperty('--lb-ratio', item.width / item.height)
    } else {
      image.style.removeProperty('--lb-ratio')
    }
    image.src = item.thumb?.currentSrc || item.src
    image.alt = item.alt
    // Transparent SVG plans get the same gray pane as in the grid (see
    // .lightbox-image.is-svg) so white/thin strokes stay visible.
    image.classList.toggle('is-svg', item.svg)
    indexEl.textContent = `${current + 1}/${items.length}`
  }

  // Swap the full-size image in over the preview (same picture, unseen). Guarded
  // so a quick navigation or a close doesn't swap in a stale slide.
  const loadFull = (item) => {
    if (image.getAttribute('src') === item.src) return
    const full = new Image()
    full.addEventListener('load', () => {
      if (items[current] === item && !overlay.hidden) image.src = item.src
    })
    full.src = item.src
  }

  // Warm the neighbours' full-size images (browser cache dedupes repeats), so
  // paging in either direction never shows the thumbnail-quality preview swap.
  // Called after loadFull so the visible slide wins the network first.
  const preloadNeighbors = () => {
    if (items.length < 2) return
    for (const dir of [1, -1]) {
      new Image().src = items[(current + dir + items.length) % items.length].src
    }
  }

  const go = (dir) => {
    current = (current + dir + items.length) % items.length
    const item = items[current]
    showPreview(item)
    loadFull(item)
    preloadNeighbors()
  }

  // FLIP transform mapping the image's laid-out box (`to`) onto a thumbnail's
  // box (`from`), so animating between it and `none` grows/shrinks the image
  // between the grid and the lightbox.
  const toThumb = (from, to) =>
    `translate(${from.left + from.width / 2 - (to.left + to.width / 2)}px,` +
    ` ${from.top + from.height / 2 - (to.top + to.height / 2)}px)` +
    ` scale(${from.width / to.width}, ${from.height / to.height})`

  const animate = (el, frames) => el.animate(frames, { duration: ZOOM_MS, easing: EASING })

  // Resolve once the image has laid out (so its box can be measured), or after a
  // short fallback — never blocks the open on a slow/undecodable image.
  const laidOut = (img) =>
    img.complete && img.naturalWidth
      ? Promise.resolve()
      : new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true })
          img.addEventListener('error', resolve, { once: true })
          setTimeout(resolve, 300)
        })

  const open = async (list, index, thumb) => {
    stopAnims()
    items = list
    current = index
    lastFocus = document.activeElement
    const item = items[current]
    showPreview(item)
    overlay.hidden = false
    document.documentElement.classList.add('is-lightbox-open')

    const from = thumb?.getBoundingClientRect()
    if (!reduceMotion && from?.width) {
      overlay.classList.add('is-zooming') // hide chrome while the image zooms
      // Let the preview lay out so the final box can be measured for the FLIP.
      await laidOut(image)
      const to = image.getBoundingClientRect()
      if (to.width) {
        const t = toThumb(from, to)
        // Pre-set the start state so the first painted frame is already at the
        // thumbnail — no flash of the full image before the zoom.
        image.style.transform = t
        backdrop.style.opacity = '0'
        anims = [
          animate(image, [{ transform: t }, { transform: 'none' }]),
          animate(backdrop, [{ opacity: 0 }, { opacity: 1 }]),
        ]
        try {
          await Promise.all(anims.map((a) => a.finished))
        } catch {
          return // cancelled by a newer interaction
        }
        anims = []
        image.style.transform = ''
        backdrop.style.opacity = ''
      }
      overlay.classList.remove('is-zooming') // chrome fades in via CSS
    }
    loadFull(item)
    preloadNeighbors()
    closeBtn.focus()
  }

  const close = async () => {
    stopAnims()
    image.style.transform = '' // clear any in-flight open transform before measuring
    const to = image.getBoundingClientRect()
    const from = items[current]?.thumb?.getBoundingClientRect()
    if (!reduceMotion && !overlay.hidden && from?.width && to.width) {
      overlay.classList.add('is-zooming') // chrome disappears as the image shrinks
      backdrop.style.opacity = ''
      anims = [
        animate(image, [{ transform: 'none' }, { transform: toThumb(from, to) }]),
        animate(backdrop, [{ opacity: 1 }, { opacity: 0 }]),
      ]
      // allSettled (not Promise.all + catch/return): the cleanup below MUST run so
      // the lightbox can never get stuck open. A cancelled or skipped animation
      // just means we finish immediately instead of blocking the close.
      await Promise.allSettled(anims.map((a) => a.finished))
      anims = []
    }
    overlay.hidden = true // set before the end-state reverts, so there's no flash
    overlay.classList.remove('is-zooming')
    image.style.transform = ''
    backdrop.style.opacity = ''
    document.documentElement.classList.remove('is-lightbox-open')
    image.removeAttribute('src')
    lastFocus?.focus()
  }

  galleries.forEach((gallery) => {
    const links = [...gallery.querySelectorAll('[data-lightbox]')]
    const list = links.map((a) => {
      const [w, h] = (a.dataset.lgSize || '').split('-').map(Number)
      return {
        src: a.getAttribute('href'),
        alt: a.querySelector('img')?.alt ?? '',
        thumb: a.querySelector('img'),
        ratio: w && h ? `${w} / ${h}` : 'auto',
        width: w || 0,
        height: h || 0,
        svg: /\.svg$/i.test((a.getAttribute('href') || '').split('?')[0]),
      }
    })
    links.forEach((a, i) =>
      a.addEventListener('click', (e) => {
        e.preventDefault()
        open(list, i, a.querySelector('img'))
      })
    )
  })

  // Close via the button or a click on the empty backdrop (never on the figure).
  closeBtn.addEventListener('click', close)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target === backdrop) close()
  })

  // One pointer pair covers mouse and touch: a horizontal drag pages
  // prev/next, a tap on the image pages by half — left half previous, right
  // half next — so a swipe is never also read as a tap.
  let startX = null
  let startY = null
  figure.addEventListener('pointerdown', (e) => {
    if (overlay.hidden) return
    startX = e.clientX
    startY = e.clientY
  })
  figure.addEventListener('pointerup', (e) => {
    if (overlay.hidden || startX === null) return
    const dx = e.clientX - startX
    const dy = e.clientY - startY
    startX = null
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1)
    else if (e.target === image) {
      const box = image.getBoundingClientRect()
      go(e.clientX < box.left + box.width / 2 ? -1 : 1)
    }
  })

  document.addEventListener('keydown', (e) => {
    if (overlay.hidden) return
    if (e.key === 'Escape') close()
    else if (e.key === 'ArrowRight') go(1)
    else if (e.key === 'ArrowLeft') go(-1)
  })
}
